import { Component, inject, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConnectVRChatComponent } from '../../dialog/connect-vrchat/connect-vrchat.component';
import { ConnectVrchatSuccessComponent } from '../../dialog/connect-vrchat-success/connect-vrchat-success.component';
import { AccountService, tiers, SubscriptionTier } from '../../service/account.service';
import { ApiService } from '../../service/api.service';
import { PowProgressComponent } from '../../helper/pow-progress/pow-progress.component';
import { PowService, PowSolution } from '../../service/pow.service';
import { ConnectPatreonPendingComponent } from '../../dialog/connect-patreon-pending/connect-patreon-pending.component';
import { ConnectPatreonFailComponent } from '../../dialog/connect-patreon-fail/connect-patreon-fail.component';
import { GuestSubmissionComponent } from '../../dialog/guest-submission/guest-submission.component';
import { ConnectVrchatFailComponent } from '../../dialog/connect-vrchat-fail/connect-vrchat-fail.component';
import { DisconnectVrchatComponent } from '../../dialog/disconnect-vrchat/disconnect-vrchat.component';
import { DisconnectPatreonComponent } from '../../dialog/disconnect-patreon/disconnect-patreon.component';
import { ConnectPatreonSuccessComponent } from '../../dialog/connect-patreon-success/connect-patreon-success.component';
import { PatreonIconComponent } from "../../helper/patreon-icon/patreon-icon.component";
import { TroubleshootPerksComponent } from '../../dialog/troubleshoot-perks/troubleshoot-perks.component';
import { AvatarComponent } from '../../helper/avatar/avatar.component';

interface PerkTier extends SubscriptionTier {
  description: string;
  features: string[];
  imageUrl: string;
}

interface Perk {
  id: number;
  name: string;
  description: string;
  features: string[];
  imageUrl: string;
}

@Component({
  selector: 'app-patreon',
  imports: [PowProgressComponent, MatCardModule, MatButtonModule, MatIconModule, AvatarComponent, RouterLink],
  templateUrl: './patreon.component.html',
  styleUrl: './patreon.component.less'
})
export class PatreonComponent implements OnInit, OnDestroy {

  readonly dialog = inject(MatDialog);
  readonly route = inject(ActivatedRoute);
  accountService: AccountService = inject(AccountService);
  apiService: ApiService = inject(ApiService);
  powService = inject(PowService);

  vrchatDialog: MatDialogRef<any> | null = null;
  patreonDialog: MatDialogRef<any> | null = null;

  // Track scroll position for floating button visibility
  showFloatingButton = true;

  // Store scroll handler for cleanup
  private scrollHandler = () => {
    this.showFloatingButton = window.scrollY <= 100;
  };

  // Store resize handler for cleanup
  private resizeHandler = () => {
    this.resetCarouselPosition();
  };

  // Carousel state management
  currentPerkIndex = 0;
  private autoRotateInterval: any;
  private readonly autoRotateDelay = 7500; // 7.5 seconds
  isTransitioning = false;
  isAutoRotateEnabled = true;
  private manualScrollDetected = false;

  perks: Perk[] = [
    {
      id: 0,
      name: "Murder 4",
      description: "Find the murderer in style",
      features: ["Golden Revolver"],
      imageUrl: "/img/perks/perks2.webp"
    },
    {
      id: 1,
      name: "Among Us",
      description: "Be the most bedazzled crewmate",
      features: ["Golden corpse", "Colored, sparkling nameplate"],
      imageUrl: "/img/perks/perks8.webp"
    },
    {
      id: 2,
      name: "Cards Against VRC",
      description: "Take the best memes to VRChat",
      features: ["Sit in a luxurious black-fabric and gold director's chair"],
      imageUrl: "/img/perks/perks3.webp"
    },
    {
      id: 3,
      name: "Price Guess",
      description: "Get exclusive cosmetics",
      features: ["Animated name plate, in the colors of your tier"],
      imageUrl: "/img/perks/perks4.webp"
    },
    {
      id: 4,
      name: "Dead Men Walking",
      description: "Get exclusive cosmetics",
      features: ["Elegant, golden weapons"],
      imageUrl: "/img/perks/perks5.webp"
    },
    {
      id: 5,
      name: "Spy Check",
      description: "Get exclusive cosmetics",
      features: ["\"Special Agent\" terminal"],
      imageUrl: "/img/perks/perks6.webp"
    },
    {
      id: 6,
      name: "Treasure Hunt Island",
      description: "Get exclusive cosmetics",
      features: ["Shiny golden tools"],
      imageUrl: "/img/perks/perks7.webp"
    },
    {
      id: 7,
      name: "Audience Anarchy",
      description: "Get exclusive cosmetics",
      features: ["Access to the private trailer", "Developer commentary"],
      imageUrl: "/img/perks/perks9.webp"
    },
    {
      id: 8,
      name: "Snowball Showdown",
      description: "Get exclusive cosmetics",
      features: ["Access to different snowball skins, depending on tier"],
      imageUrl: "/img/perks/perks10.webp"
    },
    {
      id: 9,
      name: "Magic Freeze Tag",
      description: "Get exclusive cosmetics",
      features: ["Purple crystals when freezing enemies"],
      imageUrl: "/img/perks/perks11.webp"
    },
    {
      id: 10,
      name: "Ghost Hunters",
      description: "Get exclusive cosmetics",
      features: ["Special flashlight skin"],
      imageUrl: "/img/perks/perks12.webp"
    },
    {
      id: 11,
      name: "Kitchen Cooks",
      description: "Get exclusive cosmetics",
      features: ["Gold-plated chef's knife"],
      imageUrl: "/img/perks/perks13.webp"
    },
    {
      id: 12,
      name: "Overseer",
      description: "Get exclusive cosmetics",
      features: ["Golden Staff"],
      imageUrl: "/img/perks/perks14.webp"
    },
    {
      id: 13,
      name: "Tier Land",
      description: "Get exclusive cosmetics",
      features: ["Access to the F- Tier Dumpster"],
      imageUrl: "/img/perks/perks15.webp"
    }
  ];

  // Perk data for the carousel
  availableTiers: PerkTier[] = [
    {
      id: 0,
      name: "Certified Jar Supporter",
      color: "#6897da",
      description: "Your support helps me publish more new and exciting games for you to play!",
      features: [
        "Your name in the world credits",
        "Access to the #patrons Discord channel",
      ],
      imageUrl: "/img/perks/certified-supporter.svg"
    },
    {
      id: 1,
      name: "Extra Jar Supporter",
      color: "#43867c",
      description: "Super secret behind-the-scenes game design, modeling, and animation media!",
      features: [
        "Your name in the world credits",
        "Access to the #patrons Discord channel",
        "Access to exclusive behind-the-scenes content"
      ],
      imageUrl: "/img/perks/extra-supporter.svg"
    },
    {
      id: 2,
      name: "Super Jar Supporter",
      color: "#369876",
      description: "For generous supporters who want to help fund my progress at a higher rate.",
      features: [
        "Your name in the world credits",
        "Access to the #patrons Discord channel",
        "Access to exclusive behind-the-scenes content"
      ],
      imageUrl: "/img/perks/super-supporter.svg"
    },
    {
      id: 3,
      name: "VIP Supporter",
      color: "#d4843d",
      description: "For generous supporters who want to help fund my progress at a higher rate.",
      features: [
        "Your name in the world credits",
        "Access to the #patrons Discord channel",
        "Access to exclusive behind-the-scenes content"
      ],
      imageUrl: "/img/perks/vip-supporter.svg"
    }
  ];

  get patreonCnnt() {
    return this.accountService.account.value?.connections?.patreon;
  }
  
  get vrchatCnnt() {
    return this.accountService.account.value?.connections?.vrchat;
  }

  get isVRChatConnected(): boolean {
    return !!this.vrchatCnnt;
  }

  get isPatreonConnected(): boolean {
    return !!this.patreonCnnt;
  }

  get arePerksActivated(): boolean {
    return this.isVRChatConnected && 
           this.isPatreonConnected && 
           (this.patreonCnnt?.subscriptionTier?.id ?? -1) >= 0;
  }

  get hasNoMembership(): boolean {
    return this.isPatreonConnected && 
           (this.patreonCnnt?.subscriptionTier?.id ?? -1) < 0;
  }

  get setupProgress(): number {
    let progress = 0;
    
    if (this.isVRChatConnected) progress += 33;
    if (this.isPatreonConnected) progress += 33;
    
    // Only count perks activation if user has a valid membership
    if (this.arePerksActivated) {
      progress += 33;
    } else if (this.hasNoMembership) {
      // Recalculate progress based on 2 steps instead of 3
      if (this.patreonCnnt?.subscriptionTier?.id ?? -1 < 0) {
        progress -= 33;
      }
    }
    
    if (progress == 99) progress = 100;

    return progress;
  }

  trackByTier(index: number, tier: PerkTier): number {
    return tier.id;
  }

  // Proof of work: the connect buttons stay disabled until pow is set
  pow: PowSolution | null = null;
  powError = false;

  async preparePow() {
    this.pow = null;
    this.powError = false;
    try {
      this.pow = await this.powService.solve('patreon');
    } catch (e) {
      console.error('Failed to solve Patreon challenge:', e);
      this.powError = true;
    }
  }

  connectPatreon() {
    if (!this.pow) {
      if (this.powError) this.preparePow();
      return;
    }
    const clientId = '590gN3Yo_MXYGV5MVsHnHS5qfnFwHGVXZH81OVS8u0wkbl3u2cKa3wITmZIM4AAF';
    const redirectUri = 'https://jar.games/patreon';
    const state = encodeURIComponent(JSON.stringify({
      url: window.location.href,
      pow: this.pow,
    }));

    window.location.href = `https://www.patreon.com/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=campaigns%20identity.memberships%20identity%20identity%5Bemail%5D&state=${state}`;
  }

  connectVRChat() {
    this.vrchatDialog = this.dialog.open(ConnectVRChatComponent);
    
    // Listen for dialog close
    this.vrchatDialog.afterClosed().subscribe(result => {
      if (result) {
        if (result.success) {
          this.dialog.open(ConnectVrchatSuccessComponent);
        } else {
          this.dialog.open(ConnectVrchatFailComponent, {
            data: {
              message: result.message,
              discord_url: result.discord_url,
              recovery_token: result.recovery_token,
              username: result.username
            }
          }).afterClosed().subscribe(failResult => {
            if (failResult && failResult.success) {
              this.dialog.open(ConnectVrchatSuccessComponent);
            }
          });
        }
      }
    });
  }

  disconnectVRChat() {
    this.dialog.open(DisconnectVrchatComponent);
  }

  disconnectPatreon() {
    this.dialog.open(DisconnectPatreonComponent);
  }

  ngOnInit() {
    // Check for code parameter in URL
    this.route.queryParams.subscribe(params => {

      // Needed for the connect buttons, also after returning from Patreon
      this.preparePow();

      if (Object.keys(params).length == 0) {
        return;
      }
      const code = params['code'];
      console.log(decodeURIComponent(params["state"]));
      const state = JSON.parse(decodeURIComponent(params['state']));

        
      window.history.replaceState({}, document.title, window.location.pathname);

      if (code) {
        if (state.action == "guest") {
          this.handleGuestSubmission(code, state);
        } else {
          this.handlePatreonConnect(code, state.pow);
        }
      }
    });

    // Add scroll event listener for floating button visibility
    window.addEventListener('scroll', this.scrollHandler);
    
    // Add resize event listener to reset carousel on screen size change
    window.addEventListener('resize', this.resizeHandler);

    // Initialize carousel to start from the middle set for infinite scrolling
    this.resetCarouselPosition();
    
    // Start auto-rotation for perks carousel
    this.startAutoRotate();
  }

  guestSubmission() {
    this.dialog.open(GuestSubmissionComponent);
  }

  scrollToPerks() {
    const perksSection = document.querySelector('.setup-progress-section');
    if (perksSection) {
      perksSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }

  // Get infinite carousel data (duplicated for seamless looping)
  getInfinitePerks(): Perk[] {
    // Create a triple set: [original, duplicate, duplicate] for seamless infinite scrolling
    return [...this.perks, ...this.perks, ...this.perks];
  }

  // Carousel navigation methods
  nextPerk(isAutoRotate: boolean = false) {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    this.currentPerkIndex++;
    
    if (isAutoRotate) {
      this.startProgressAnimation();
    }
    
    // If we're at the end of the second set, jump to the beginning of the second set
    if (this.currentPerkIndex >= this.perks.length * 2) {
      setTimeout(() => {
        this.currentPerkIndex = this.perks.length;
        this.isTransitioning = false;
      }, 500); // Wait for transition to complete
    } else {
      setTimeout(() => {
        this.isTransitioning = false;
      }, 500);
    }
    
    if (!isAutoRotate) {
      this.onManualInteraction('nextPerk');
    }
  }

  previousPerk() {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    this.currentPerkIndex--;
    
    // If we're at the beginning of the first set, jump to the end of the second set
    if (this.currentPerkIndex < 0) {
      setTimeout(() => {
        this.currentPerkIndex = this.perks.length - 1;
        this.isTransitioning = false;
      }, 500); // Wait for transition to complete
    } else {
      setTimeout(() => {
        this.isTransitioning = false;
      }, 500);
    }
    
    this.onManualInteraction('previousPerk');
  }

  goToPerk(index: number) {
    if (this.isTransitioning) return;
    
    const visibleCards = this.getVisibleCardsCount();
    // For 1 card: center is at index 0 (single card)
    // For 3 cards: center is at index 1 (middle card)
    const centerOffset = visibleCards === 1 ? 0 : Math.floor(visibleCards / 2);
    // Position the carousel so the selected perk appears in the center
    this.currentPerkIndex = index + this.perks.length - centerOffset;
    this.onManualInteraction('goToPerk');
  }

  // Get the transform value for the carousel
  getCarouselTransform(): string {
    const visibleCards = this.getVisibleCardsCount();
    const cardWidth = 100 / visibleCards;
    return `translateX(${-this.currentPerkIndex * cardWidth}%)`;
  }

  // Get the number of visible cards based on screen size
  private getVisibleCardsCount(): number {
    return window.innerWidth <= 768 ? 1 : 3;
  }

  // Reset carousel to a clean position
  private resetCarouselPosition() {
    const visibleCards = this.getVisibleCardsCount();
    const centerOffset = visibleCards === 1 ? 0 : Math.floor(visibleCards / 2);
    this.currentPerkIndex = this.perks.length - centerOffset;
  }

  // Get the actual perk index for indicators (0 to perks.length - 1)
  // This should represent the center card position
  getActualPerkIndex(): number {
    const visibleCards = this.getVisibleCardsCount();
    // For 1 card: center is at index 0 (single card)
    // For 3 cards: center is at index 1 (middle card)
    const centerOffset = visibleCards === 1 ? 0 : Math.floor(visibleCards / 2);
    const centerCardIndex = this.currentPerkIndex + centerOffset;
    return centerCardIndex % this.perks.length;
  }

  private startAutoRotate() {
    if (!this.isAutoRotateEnabled) return;
    
    this.autoRotateInterval = setInterval(() => {
      if (this.isAutoRotateEnabled && !this.manualScrollDetected) {
        this.nextPerk(true);
      }
    }, this.autoRotateDelay);
    
    // Start the first progress animation after a short delay to account for initial load
    setTimeout(() => {
      if (this.isAutoRotateEnabled) {
        this.startProgressAnimation();
      }
    }, 500);
  }

  private stopAutoRotate() {
    if (this.autoRotateInterval) {
      clearInterval(this.autoRotateInterval);
      this.autoRotateInterval = null;
    }
    this.stopProgressAnimation();
  }

  private resetAutoRotate() {
    this.stopAutoRotate();
    this.startAutoRotate();
  }

  private startProgressAnimation() {
    const buttons = document.querySelectorAll('.auto-rotate') as NodeListOf<HTMLElement>;
    buttons.forEach(button => {
      // Set fast transition duration for reset
      button.style.setProperty('--transition-duration', '0.1s');
      // Reset to 0% width
      button.style.setProperty('--progress-width', '0%');
      
      // Wait for the reset to complete, then start the fill
      setTimeout(() => {
        // Force a reflow to ensure the reset is applied
        button.offsetHeight;
        // Set transition duration for fill (4.7s to complete before scroll)
        button.style.setProperty('--transition-duration', '7s');
        // Set target to 100% width
        button.style.setProperty('--progress-width', '100%');
      }, 150); // Wait for 0.1s transition + buffer
    });
  }

  private stopProgressAnimation() {
    const buttons = document.querySelectorAll('.auto-rotate') as NodeListOf<HTMLElement>;
    buttons.forEach(button => {
      button.style.setProperty('--progress-width', '0%');
    });
  }

  onManualInteraction(type: string) {
    this.manualScrollDetected = true;
    this.isAutoRotateEnabled = false;
    this.stopAutoRotate();
    
    // Ensure carousel is in a valid state after manual interaction
    setTimeout(() => {
      this.ensureValidCarouselState();
    }, 100);
  }

  // Ensure carousel is in a valid state
  private ensureValidCarouselState() {
    const visibleCards = this.getVisibleCardsCount();
    const totalCards = this.perks.length * 3; // Triple set for infinite scrolling
    
    // If we're outside the valid range, reset to a safe position
    if (this.currentPerkIndex < 0 || this.currentPerkIndex >= totalCards) {
      this.resetCarouselPosition();
    }
  }

  ngOnDestroy() {
    // Remove scroll event listener
    window.removeEventListener('scroll', this.scrollHandler);
    
    // Remove resize event listener
    window.removeEventListener('resize', this.resizeHandler);
    
    // Stop auto-rotation and progress animation
    this.stopAutoRotate();
  }


  private handleGuestSubmission(code: string, state: any) {
    // Show loading dialog or handle the guest submission process
    console.log("Guest submission", code, state);
    
    // Make API call to guest submission endpoint using ApiService
    this.apiService.post('account/guest_submision.php', {
      code: code,
      pow: state.pow,
      vrchat_name: state.vrchat_username
    }).subscribe((data: any) => {
      if (data.code === 200) {
        console.log("Guest submission success", data);
        // Success - show success dialog
        if (data.guest_submission.patreon_tier >= 0) {
          console.log("Showing the success dialog");
          this.dialog.open(ConnectPatreonSuccessComponent, {
            data: {
              patreon_account_name: data.guest_submission.patreon_username,
              patreon_tier: tiers[data.guest_submission.patreon_tier.toString()],
              isGuest: true,
              expireDate: data.guest_submission.patreon_expire,
              vrchat_name: data.guest_submission.vrchat_name
            }
          });
        } else {
          console.log("Showing the fail dialog because ", data.guest_submission.patreon_tier, " is less than 0");
          this.dialog.open(ConnectPatreonFailComponent, {
            data: {
              message: "The Patreon account you have linked does not have a Patreon subscription. Please verify that you have an active paid subscription and that you have linked the correct account!"
            }
          });
        }
      } else {
        // Error - show error dialog
        this.dialog.open(ConnectPatreonFailComponent, {
          data: {
            message: data.message || 'Guest submission failed. Please try again.'
          }
        });
      }
    }, (error: any) => {
      console.error('Guest submission error:', error);
      this.dialog.open(ConnectPatreonFailComponent, {
        data: {
          message: 'Failed to submit guest application. Please try again.'
        }
      });
    });
  }

  troubleshootPerks() {
    this.dialog.open(TroubleshootPerksComponent);
  }

  private handlePatreonConnect(code: string, pow: PowSolution) {

    this.patreonDialog = this.dialog.open(ConnectPatreonPendingComponent, {
      data: {
        oauth_code: code,
        pow: pow
      }
    });

    this.patreonDialog.afterClosed().subscribe(result => {
      if (result) {
        if (result.success) {
          this.dialog.open(ConnectPatreonSuccessComponent, {
            data: {
              patreon_account_name: this.patreonCnnt?.username,
              patreon_tier: this.patreonCnnt?.subscriptionTier
            }
          });
        } else {
          this.dialog.open(ConnectPatreonFailComponent, {
            data: {
              message: "There was an error linking your Patreon account to your jar.games account. Please try again."
            }
          });
        }
      }
    });


  }
}